/**
 * Visibility queries.
 *
 * Calls /api/visibility/projects and /api/visibility/inbox — the cross-project
 * rollups built in Pass 11. These power the Dashboard, Projects list, and
 * Inbox views.
 */
import { useQuery } from '@tanstack/react-query';
import { apiRequest } from './api-client';

// ---------------------------------------------------------------------------
// Types (mirror packages/shared but inlined here so the web app doesn't depend
// on the TypeBox runtime — only the inferred wire shapes)
// ---------------------------------------------------------------------------

export type ProjectLifecycleState =
  | 'DFA_RECEIVED'
  | 'DFA_SUBMITTED'
  | 'CONTRACT_SIGNED'
  | 'PENDING_PARTS_APPROVAL'
  | 'ACTIVE'
  | 'AWAITING_PAYMENT'
  | 'CLOSED';

export interface VisibilityRow {
  projectId: string;
  projectNumber: string;
  projectTitle: string;
  clientId: string;
  clientName: string;
  lifecycleState: ProjectLifecycleState;
  isOverdue: boolean;
  isClosed: boolean;
  dfaReceivedDate: string;
  deliveryDurationMonths: number | null;
  effectiveDeliveryTargetDate: string | null;
  daysSinceUpdate: number;
  lastUpdatedAt: string;
  partCount: number;
  activeBatchCount: number;
  overdueBatchCount: number;
  unresolvedInspectionCount: number;
  hasPendingPartsApproval: boolean;
  /** E20: distribution of parts across the four phase buckets. */
  partsProgress: {
    procurement: number;
    inspection: number;
    billing: number;
    closed: number;
  };
}

export interface VisibilityListResponse {
  items: VisibilityRow[];
  total: number;
  page: number;
  pageSize: number;
}

export type ProjectsSortKey =
  | 'projectNumber'
  | 'projectTitle'
  | 'updatedAt'
  | 'effectiveDeliveryTargetDate'
  | 'clientName';

export interface VisibilityListQuery {
  page?: number;
  pageSize?: number;
  lifecycleState?: ProjectLifecycleState;
  clientId?: string;
  search?: string;
  overdueOnly?: boolean;
  excludeClosed?: boolean;
  // Patch 5 — sortable columns
  sortBy?: ProjectsSortKey;
  sortDir?: 'asc' | 'desc';
}

export interface InboxPendingApproval {
  projectId: string;
  projectNumber: string;
  projectTitle: string;
  submittedAt: string;
  submittedByDisplayName: string;
}

export interface InboxResponse {
  pendingPartsApprovals: InboxPendingApproval[];
  overdueProjects: VisibilityRow[];
  unreadNotificationCount: number;
}

// ---------------------------------------------------------------------------
// Awaiting-action queue (spec §10.1, patch D1)
// ---------------------------------------------------------------------------

export interface AwaitingActionItem {
  entityKind: 'PART' | 'BATCH';
  entityId: string;
  projectId: string;
  projectNumber: string;
  projectTitle: string;
  projectItemId: string;
  itemName: string;
  orderBatchId: string | null;
  supplierName: string | null;
  currentStageNumber: number;
  nextStageNumber: number;
  nextStageLabel: string;
  daysInCurrentStage: number;
}

export interface AwaitingActionResponse {
  items: AwaitingActionItem[];
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

export const projectsListQueryKey = (q: VisibilityListQuery) =>
  ['visibility', 'projects', q] as const;

export function useProjectsList(query: VisibilityListQuery = {}) {
  return useQuery({
    queryKey: projectsListQueryKey(query),
    queryFn: ({ signal }) =>
      apiRequest<VisibilityListResponse>('/api/visibility/projects', {
        query: {
          page: query.page,
          pageSize: query.pageSize,
          lifecycleState: query.lifecycleState,
          clientId: query.clientId,
          search: query.search,
          overdueOnly: query.overdueOnly,
          excludeClosed: query.excludeClosed,
          sortBy: query.sortBy,
          sortDir: query.sortDir,
        },
        signal,
      }),
    placeholderData: (prev) => prev,
  });
}

export const inboxQueryKey = ['visibility', 'inbox'] as const;

export function useInbox(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: inboxQueryKey,
    queryFn: ({ signal }) => apiRequest<InboxResponse>('/api/visibility/inbox', { signal }),
    enabled: options.enabled ?? true,
  });
}

// ---------------------------------------------------------------------------
// E21: Receivables / pending-status
// ---------------------------------------------------------------------------

export interface ReceivablesBgRow {
  bgId: string;
  projectId: string;
  projectNumber: string;
  projectTitle: string;
  clientName: string;
  amountPaisa: number;
  submittedDate: string;
  daysSinceSubmitted: number;
}

export interface ReceivablesPaymentRow {
  projectId: string;
  projectNumber: string;
  projectTitle: string;
  clientName: string;
  partsAwaitingPayment: number;
  partsChequeReleased: number;
  partsChequeNotReleased: number;
  oldestInvoiceDate: string;
  daysSinceOldestInvoice: number;
}

export interface ReceivablesEarnestRow {
  projectId: string;
  projectNumber: string;
  projectTitle: string;
  clientName: string;
  earnestAmountPaisa: number | null;
  earnestSubmittedDate: string;
  daysSinceSubmitted: number;
}

export interface ReceivablesResponse {
  bgAwaitingAcceptance: ReceivablesBgRow[];
  awaitingPayment: ReceivablesPaymentRow[];
  earnestAwaitingRelease: ReceivablesEarnestRow[];
  aggregate: {
    activeBgExposurePaisa: number;
  };
}

export const receivablesQueryKey = ['visibility', 'receivables'] as const;

export function useReceivables() {
  return useQuery({
    queryKey: receivablesQueryKey,
    queryFn: ({ signal }) =>
      apiRequest<ReceivablesResponse>('/api/visibility/receivables', { signal }),
  });
}

// ---------------------------------------------------------------------------
// E25: Throughput sparkline (projects closed per PKT month, rolling 6mo)
// ---------------------------------------------------------------------------

export interface ThroughputMonth {
  yearMonth: string; // 'YYYY-MM'
  closedCount: number;
}

export interface ThroughputResponse {
  months: ThroughputMonth[];
  currentMonthClosed: number;
  trailingAverage: number;
}

export const throughputQueryKey = ['visibility', 'throughput'] as const;

export function useThroughput() {
  return useQuery({
    queryKey: throughputQueryKey,
    queryFn: ({ signal }) =>
      apiRequest<ThroughputResponse>('/api/visibility/throughput', { signal }),
  });
}

export const awaitingActionQueryKey = ['visibility', 'awaiting-action'] as const;

export function useAwaitingAction() {
  return useQuery({
    queryKey: awaitingActionQueryKey,
    queryFn: ({ signal }) =>
      apiRequest<AwaitingActionResponse>('/api/visibility/awaiting-action', { signal }),
  });
}

// ---------------------------------------------------------------------------
// Entity kanban (spec §10.2, patch D2)
// ---------------------------------------------------------------------------

export interface EntityKanbanItem {
  entityKind: 'PART' | 'SUB_PART';
  entityId: string;
  projectId: string;
  projectNumber: string;
  projectTitle: string;
  itemName: string;
  parentItemId: string | null;
  parentItemName: string | null;
  currentStageNumber: number;
  currentStageLabel: string;
  daysInCurrentStage: number;
}

export interface EntityKanbanResponse {
  items: EntityKanbanItem[];
}

export const entityKanbanQueryKey = ['visibility', 'entity-kanban'] as const;

export function useEntityKanban() {
  return useQuery({
    queryKey: entityKanbanQueryKey,
    queryFn: ({ signal }) =>
      apiRequest<EntityKanbanResponse>('/api/visibility/entity-kanban', { signal }),
  });
}

// ---------------------------------------------------------------------------
// BG / Warranty alerts (spec §10.4 / §14 #3, patch E11)
// ---------------------------------------------------------------------------

export interface AlertProject {
  projectId: string;
  projectNumber: string;
  projectTitle: string;
  warrantyEndDate: string;
  /** Positive when in the future (approaching), negative when past (overdue). */
  daysToWarrantyEnd: number;
  /** Sum of ACCEPTED BG amounts in paisa; null when no ACCEPTED BG exists. */
  activeBgAmountPaisa: number | null;
}

export interface AlertsResponse {
  warrantyApproaching: AlertProject[];
  bgReleaseOverdue: AlertProject[];
  ndcPending: AlertProject[];
}

export const alertsQueryKey = ['visibility', 'alerts'] as const;

export function useAlerts() {
  return useQuery({
    queryKey: alertsQueryKey,
    queryFn: ({ signal }) =>
      apiRequest<AlertsResponse>('/api/visibility/alerts', { signal }),
  });
}
